import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiCalendar,
  FiEdit2,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiUser,
  FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    address: user?.address || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateProfile(formData);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleCancel = () => {
    setFormData({
      displayName: user?.displayName || '',
      email: user?.email || '',
      phone: user?.phoneNumber || '',
      address: user?.address || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="sc-container pb-16 pt-8 md:pt-10">
      <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500">
        <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-700">Profile</span>
      </nav>

      <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-12">
        <article className="sc-soft-card relative overflow-hidden p-6 sm:p-7 lg:col-span-8">
          <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#DB4444]/10 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-slate-900/5 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DB4444]/10 text-[#DB4444]">
                <FiUser size={30} />
              </div>
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Account Hub</p>
                <h1 className="text-3xl font-black text-[#1A1A1A]">My Profile</h1>
                <p className="mt-1 text-sm text-slate-600">Manage your personal and account information.</p>
              </div>
            </div>
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="sc-btn-primary sc-hover-smooth inline-flex items-center gap-2">
                <FiEdit2 size={14} />
                Edit Profile
              </button>
            ) : (
              <div className="flex flex-wrap gap-3">
                <button onClick={handleSave} className="sc-btn-primary sc-hover-smooth inline-flex items-center gap-2">
                  <FiSave size={14} />
                  Save Changes
                </button>
                <button onClick={handleCancel} className="sc-btn-ghost sc-hover-smooth inline-flex items-center gap-2">
                  <FiX size={14} />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </article>

        <article className="sc-soft-card p-6 lg:col-span-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#DB4444]">Account Status</p>
          <div className="space-y-3 text-sm text-slate-600">
            <div className="sc-chip">
              <FiLock size={13} />
              Email verified identity
            </div>
            <div className="sc-chip">
              <FiCalendar size={13} />
              Joined {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}
            </div>
            <div className="sc-chip">
              <FiCalendar size={13} />
              Last sign in {user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </article>
      </section>

      <section className="sc-soft-card p-6 sm:p-8">
        <div className="mb-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Personal Information</p>
          <h2 className="text-2xl font-black text-[#1A1A1A]">Profile Details</h2>
          <p className="mt-1 text-sm text-slate-600">Keep your details up to date for faster checkout and support.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FieldLabel icon={<FiUser size={14} />} label="Full Name" />
          <FieldLabel icon={<FiMail size={14} />} label="Email Address" />
          {isEditing ? (
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className="sc-input md:col-span-1"
              placeholder="Enter your full name"
            />
          ) : (
            <DisplayField value={formData.displayName} />
          )}
          <DisplayField value={formData.email || user?.email} hint="Email cannot be changed" />

          <FieldLabel icon={<FiPhone size={14} />} label="Phone Number" />
          <FieldLabel icon={<FiMapPin size={14} />} label="Address" />
          {isEditing ? (
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="sc-input"
              placeholder="Enter your phone number"
            />
          ) : (
            <DisplayField value={formData.phone} />
          )}
          {isEditing ? (
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className="sc-input min-h-[108px] resize-y"
              placeholder="Enter your address"
            />
          ) : (
            <DisplayField value={formData.address} className="min-h-[108px]" />
          )}
        </div>
      </section>
    </div>
  );
};

const FieldLabel = ({ icon, label }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#DB4444]/10 text-[#DB4444]">
      {icon}
    </span>
    {label}
  </div>
);

const DisplayField = ({ value, hint, className = '' }) => (
  <div>
    <div className={`rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 ${className}`}>
      {value || 'Not provided'}
    </div>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export default Profile;
